import { EventEmitter } from '../utils/events.js';

/**
 * Flight data store with event emission
 */
export class FlightStore extends EventEmitter {
  constructor() {
    super();
    this.flights = new Map();
    this.lastUpdate = null;
  }

  /**
   * Update flights from server data
   * @param {Array} flightsData - Array of flight objects
   */
  updateFlights(flightsData) {
    const currentIds = new Set();

    for (const flight of flightsData) {
      currentIds.add(flight.id);
      this.flights.set(flight.id, flight);
    }

    // Check for removed flights
    for (const [id] of this.flights) {
      if (!currentIds.has(id)) {
        this.flights.delete(id);
        this.emit('remove', id);
      }
    }

    this.lastUpdate = Date.now();
    this.emit('update', this.getAllFlights());
  }

  /**
   * Remove a single flight
   * @param {string} flightId
   */
  removeFlight(flightId) {
    if (this.flights.has(flightId)) {
      this.flights.delete(flightId);
      this.emit('remove', flightId);
    }
  }

  /**
   * Clear all flights (used when changing regions)
   */
  clear() {
    for (const id of this.flights.keys()) {
      this.emit('remove', id);
    }
    this.flights.clear();
    this.lastUpdate = null;
  }

  /**
   * Get a single flight by ID
   * @param {string} flightId
   * @returns {Object|undefined}
   */
  getFlight(flightId) {
    return this.flights.get(flightId);
  }

  /**
   * Get all flights as array
   * @returns {Array}
   */
  getAllFlights() {
    return Array.from(this.flights.values());
  }

  /**
   * Get count of current flights (excluding vanished ones that are only
   * kept around for the timeline)
   * @returns {number}
   */
  getFlightCount() {
    let count = 0;
    for (const flight of this.flights.values()) {
      if (!flight.gone) count++;
    }
    return count;
  }

  /**
   * Get flights filtered by criteria
   * @param {Object} filters
   * @returns {Array}
   */
  getFilteredFlights(filters = {}) {
    let flights = this.getAllFlights();

    if (filters.hideGround) {
      flights = flights.filter(f => !f.onGround);
    }

    if (filters.minAltitude) {
      flights = flights.filter(f => f.position.altitude >= filters.minAltitude);
    }

    if (filters.maxAltitude) {
      flights = flights.filter(f => f.position.altitude <= filters.maxAltitude);
    }

    if (filters.phase) {
      flights = flights.filter(f => f.phase === filters.phase);
    }

    return flights;
  }
}
